import React from 'react';

import { Link, useNavigate } from 'react-router-dom';

import { Collapse, Empty, notification } from 'antd';

import { ASSET_TYPES, getAssetTypeName } from '@/shared/api/openmetadata';
import { getAssetIconStyle } from '@/shared/lib/asset';
import { buildStandaloneAssetFqn } from '@/shared/lib/constants/experimentAssets';
import { convertFqnToPath } from '@/shared/lib/format/fqn';
import { buildAssetDetailUrl } from '@/shared/lib/url';
import { DrawerDescription } from '@/shared/ui/AssetMetadata';
import { EllipsisText } from '@/shared/ui/EllipsisText';
import { ErrorFallback } from '@/shared/ui/ErrorBoundary';
import { Icon } from '@/shared/ui/Icon';
import type { DrawerSection, InfoRow } from '@/shared/ui/lineage/LineageNodeDrawer';
import { LoadingState } from '@/shared/ui/states/LoadingState';
import { Tooltip } from '@/shared/ui/Tooltip';

import type { SearchResult } from '@/entities/search';

import styles from './SearchPreviewPanel.module.css';

function isPlainInfoValue(value: InfoRow['value']): value is string | number {
  return typeof value === 'string' || typeof value === 'number';
}

type SearchPreviewPanelProps = {
  selectedResult: SearchResult | null;
  infoRows: InfoRow[];
  sections: DrawerSection[];
  description?: string | null;
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
};

export const SearchPreviewPanel: React.FC<SearchPreviewPanelProps> = ({
  selectedResult,
  infoRows,
  sections,
  description,
  isLoading,
  isError,
  onRetry,
}) => {
  const navigate = useNavigate();

  const isRun = selectedResult?.type === ASSET_TYPES.RUN;
  const isExperiment = selectedResult?.type === ASSET_TYPES.EXPERIMENT;

  const transformedPath = React.useMemo(() => {
    if (!selectedResult) return '';

    const assetPath = selectedResult._source?.assetPath;

    if (assetPath && Array.isArray(assetPath) && assetPath.length > 0) {
      return assetPath.map((p) => p.label).join(' / ');
    }

    if (isRun) {
      return `Experiments / ${selectedResult.experimentName || 'Unknown'}`;
    }
    if (isExperiment) {
      return 'Experiments';
    }
    return convertFqnToPath(selectedResult.path);
  }, [selectedResult, isRun, isExperiment]);

  if (!selectedResult) {
    return (
      <div className={styles.previewPanel}>
        <div className={styles.emptyState}>
          <Empty description="선택된 항목이 없습니다" />
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={styles.previewPanel}>
        <LoadingState message="데이터를 불러오는 중..." />
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles.previewPanel}>
        <div className={styles.errorState}>
          <ErrorFallback
            error={{ name: 'Preview Load Error', message: '조회에 실패했습니다. 다시 시도해 주세요.' }}
            resetErrorBoundary={onRetry}
          />
        </div>
      </div>
    );
  }

  const handleStandaloneAsset = (
    assetType: 'dataset' | 'feature_set',
    assetData: { snapshotId?: number; tier: string; namespace: string; table: string } | undefined
  ) => {
    if (!assetData) return false;

    try {
      if (assetData.snapshotId) {
        const url = new URL(selectedResult.path, window.location.origin);
        url.searchParams.set('snapshotId', String(assetData.snapshotId));
        navigate(url.pathname + url.search);
      } else {
        const fqn = buildStandaloneAssetFqn(assetData.tier, assetData.namespace, assetData.table);
        navigate(`/${assetType}/${fqn}/columns`);
      }
      return true;
    } catch (error) {
      console.error('Failed to navigate to standalone asset:', error);
      return false;
    }
  };

  const handleNavigate = () => {
    try {
      const rawAssetItem = selectedResult._source?.rawAssetItem;

      if (
        selectedResult.type === ASSET_TYPES.FEATURE_SET &&
        handleStandaloneAsset(ASSET_TYPES.FEATURE_SET, rawAssetItem?.featureSet)
      ) {
        return;
      }

      if (
        selectedResult.type === ASSET_TYPES.DATASET &&
        handleStandaloneAsset(ASSET_TYPES.DATASET, rawAssetItem?.dataset)
      ) {
        return;
      }

      if (selectedResult.path.startsWith('/experiments/')) {
        navigate(selectedResult.path);
      } else {
        const detailUrl = buildAssetDetailUrl(selectedResult.type, selectedResult.path, undefined, selectedResult.id);
        navigate(detailUrl);
      }
    } catch (error) {
      console.error('Navigation failed:', error);
      notification.error({
        message: '이동 실패',
        description: '이동할 수 없습니다. 다시 시도해 주세요.',
        duration: 5,
        placement: 'bottomLeft',
      });
    }
  };

  const { backgroundColor, borderColor, iconColor } = getAssetIconStyle(selectedResult.type);

  const iconWrapperStyle = {
    backgroundColor,
    border: `1px solid ${borderColor}`,
  };

  return (
    <div className={styles.previewPanel}>
      <div className={styles.previewHeader}>
        <div className={styles.headerCard}>
          <Tooltip title={getAssetTypeName(selectedResult.type)} placement="bottom" arrow={false}>
            <div className={styles.iconWrapper} style={iconWrapperStyle}>
              <Icon assetType={selectedResult.type} size={17.5} color={iconColor} />
            </div>
          </Tooltip>
          <div className={styles.titleInfo}>
            <div className={styles.pathRow}>
              {transformedPath && <EllipsisText text={transformedPath} className={styles.pathText} />}
            </div>
            <div className={styles.titleRow}>
              <EllipsisText text={selectedResult.title} className={styles.titleText}>
                <span onClick={handleNavigate}>{selectedResult.title}</span>
              </EllipsisText>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.previewContent}>
        {description && (
          <div className={styles.descriptionSection}>
            <DrawerDescription description={description} />
          </div>
        )}

        {infoRows.length > 0 && (
          <div className={styles.infoSection}>
            {infoRows.map((row) => (
              <div key={row.label} className={styles.infoRow}>
                <span className={styles.infoLabel}>
                  <EllipsisText text={row.label} tooltipPlacement="topLeft" />
                </span>
                {row.linkTo ? (
                  <Link to={row.linkTo} className={styles.infoLink}>
                    {isPlainInfoValue(row.value) ? (
                      <EllipsisText text={String(row.value)} tooltipPlacement="topRight" />
                    ) : (
                      row.value
                    )}
                  </Link>
                ) : (
                  <span className={styles.infoValue}>
                    {isPlainInfoValue(row.value) ? (
                      <EllipsisText text={String(row.value)} tooltipPlacement="topRight" />
                    ) : (
                      row.value
                    )}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        {sections.map((section) =>
          section.collapsible ? (
            <Collapse
              key={section.id}
              className={styles.section}
              items={[
                {
                  key: section.id,
                  label: section.title,
                  children: section.content,
                },
              ]}
              defaultActiveKey={[section.id]}
              ghost
              expandIconPosition="end"
              expandIcon={({ isActive }) => <Icon name={isActive ? 'arrow-short-down' : 'arrow-short-up'} size={16} />}
            />
          ) : (
            <div key={section.id} className={styles.section}>
              <h3 className={styles.sectionTitle}>{section.title}</h3>
              {section.content}
            </div>
          )
        )}
      </div>
    </div>
  );
};
